/**
 * Utility-Funktionen für Urlaubsblock-Buchungen
 *
 * Erzeugt, filtert und verwaltet Urlaubstage über einen Datumsbereich.
 * Wochenenden, Feiertage und Krankheitstage werden automatisch übersprungen.
 */

import { addDays, format, parseISO, isWeekend } from 'date-fns';
import type { DayEntry } from '../types';

export interface VacationRangeDay {
  /** Datum im ISO-Format YYYY-MM-DD */
  date: string;
  /** Ist ein Samstag oder Sonntag */
  isWeekend: boolean;
  /** Ist ein Feiertag */
  isHoliday: boolean;
  /** Ist ein Krankheitstag */
  isSickDay: boolean;
  /** Ist bereits als Urlaub eingetragen */
  isAlreadyVacation: boolean;
  /** Ist Betriebsferientag */
  isCompanyVacation: boolean;
  /** Wird beim Eintragen übersprungen (Wochenende, Feiertag oder Krankheit) */
  willBeSkipped: boolean;
  /** Halber Urlaubstag (nur erster/letzter Tag, wenn vom User gewählt) */
  isHalfDay: boolean;
  /** Grund für das Überspringen (für Anzeige) */
  skipReason?: string;
}

export interface VacationRangeOptions {
  /** Erster Tag halber Urlaub? */
  halfDayStart: boolean;
  /** Letzter Tag halber Urlaub? */
  halfDayEnd: boolean;
}

/**
 * Generiert alle Tage im Bereich mit ihrem Status.
 */
export function generateVacationRange(
  startDate: string,
  endDate: string,
  entries: Record<string, DayEntry>,
  holidays: string[],
  options: VacationRangeOptions
): VacationRangeDay[] {
  const start = parseISO(startDate);
  const end = parseISO(endDate);

  if (start > end) return [];

  const holidaySet = new Set(holidays);
  const result: VacationRangeDay[] = [];

  let current = start;
  while (current <= end) {
    const dateStr = format(current, 'yyyy-MM-dd');
    const entry = entries[dateStr];
    const weekend = isWeekend(current);
    const holiday = entry?.isHoliday || holidaySet.has(dateStr);
    const sickDay = entry?.isSickDay || false;
    const alreadyVacation = entry?.isVacation || entry?.isHalfVacation || false;
    const companyVacation = entry?.isCompanyVacation || false;

    const willBeSkipped = weekend || holiday || sickDay;

    let skipReason: string | undefined;
    if (weekend) skipReason = 'Wochenende';
    else if (holiday) skipReason = 'Feiertag';
    else if (sickDay) skipReason = 'Krankheit';

    const isFirst = dateStr === startDate;
    const isLast = dateStr === endDate;
    const isSingleDay = startDate === endDate;

    let isHalfDay = false;
    if (!willBeSkipped) {
      if (isSingleDay) {
        isHalfDay = options.halfDayStart;
      } else {
        if (isFirst) isHalfDay = options.halfDayStart;
        if (isLast) isHalfDay = options.halfDayEnd;
      }
    }

    result.push({
      date: dateStr,
      isWeekend: weekend,
      isHoliday: holiday,
      isSickDay: sickDay,
      isAlreadyVacation: alreadyVacation,
      isCompanyVacation: companyVacation,
      willBeSkipped,
      isHalfDay,
      skipReason,
    });

    current = addDays(current, 1);
  }

  return result;
}

/**
 * Filtert nur die tatsächlich buchbaren Tage (ohne übersprungene).
 */
export function getBookableDays(range: VacationRangeDay[]): VacationRangeDay[] {
  return range.filter((day) => !day.willBeSkipped);
}

/**
 * Erzeugt aktualisierte Entries für den Urlaubsbereich (Eintragen-Modus).
 * Bestehende Entries bleiben erhalten (Merge), nur Urlaubsfelder werden gesetzt.
 */
export function createVacationEntries(
  bookableDays: VacationRangeDay[],
  existingEntries: Record<string, DayEntry>,
  absenceTypeId?: string
): Record<string, DayEntry> {
  const updated = { ...existingEntries };

  for (const day of bookableDays) {
    const existing = updated[day.date];

    // Betriebsferien nicht überschreiben
    if (existing?.isCompanyVacation) {
      continue;
    }

    updated[day.date] = {
      ...existing,
      date: day.date,
      hours: existing?.hours || 0,
      isHoliday: existing?.isHoliday || false,
      isVacation: !day.isHalfDay,
      isHalfVacation: day.isHalfDay,
      isSickDay: false,
      absenceTypeId: absenceTypeId ?? existing?.absenceTypeId,
      // Bestehende Notes und Manager-Notes beibehalten
      note: existing?.note,
      managerNote: existing?.managerNote,
      managerNoteAuthorId: existing?.managerNoteAuthorId,
      managerNoteAuthorName: existing?.managerNoteAuthorName,
      managerNoteTimestamp: existing?.managerNoteTimestamp,
    };
  }

  return updated;
}

/**
 * Entfernt Urlaub aus einem Bereich.
 * Betriebsferien (isCompanyVacation) werden NICHT entfernt.
 */
export function removeVacationFromRange(
  startDate: string,
  endDate: string,
  existingEntries: Record<string, DayEntry>
): Record<string, DayEntry> {
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  const updated = { ...existingEntries };

  let current = start;
  while (current <= end) {
    const dateStr = format(current, 'yyyy-MM-dd');
    const entry = updated[dateStr];

    if (entry && (entry.isVacation || entry.isHalfVacation) && !entry.isCompanyVacation) {
      updated[dateStr] = {
        ...entry,
        isVacation: false,
        isHalfVacation: false,
        // vacationCalculatedHours zurücksetzen
        vacationCalculatedHours: undefined,
      };
    }

    current = addDays(current, 1);
  }

  return updated;
}

/**
 * Zählt die Urlaubstage die im Bereich entfernt werden würden.
 */
export function countRemovableDays(
  startDate: string,
  endDate: string,
  entries: Record<string, DayEntry>
): { fullDays: number; halfDays: number; total: number } {
  const start = parseISO(startDate);
  const end = parseISO(endDate);

  let fullDays = 0;
  let halfDays = 0;

  let current = start;
  while (current <= end) {
    const dateStr = format(current, 'yyyy-MM-dd');
    const entry = entries[dateStr];

    if (entry && !entry.isCompanyVacation) {
      if (entry.isVacation) fullDays++;
      else if (entry.isHalfVacation) halfDays++;
    }

    current = addDays(current, 1);
  }

  return { fullDays, halfDays, total: fullDays + halfDays * 0.5 };
}

/**
 * Berechnet zusammenfassende Statistiken für eine Urlaubsblock-Vorschau.
 */
export function calculateRangeSummary(range: VacationRangeDay[]) {
  const bookable = getBookableDays(range);
  const skipped = range.filter((d) => d.willBeSkipped);

  const fullDays = bookable.filter((d) => !d.isHalfDay).length;
  const halfDays = bookable.filter((d) => d.isHalfDay).length;
  const totalVacationDays = fullDays + halfDays * 0.5;

  const weekends = skipped.filter((d) => d.isWeekend).length;
  const holidays = skipped.filter((d) => d.isHoliday && !d.isWeekend).length;
  const sickDays = skipped.filter((d) => d.isSickDay && !d.isWeekend && !d.isHoliday).length;

  const alreadyVacation = bookable.filter((d) => d.isAlreadyVacation).length;

  return {
    totalDays: range.length,
    bookableDays: bookable.length,
    fullDays,
    halfDays,
    totalVacationDays,
    skippedDays: skipped.length,
    weekends,
    holidays,
    sickDays,
    alreadyVacation,
  };
}
